import React from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { PageFrame } from '../components/PageFrame';
import { NavigationControls } from '../components/NavigationControls';
import { Mail, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { trackReportGenerated } from '../utils/analytics';
import { ReportGenerationProgress } from '../components/ReportGenerationProgress';
import { getApiUrl } from '../utils/apiBase';
import { auditSessionHeaders, getAuditSessionId } from '../components/ProtectedAuditRoute';

const API = getApiUrl();

export default function ActionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditData, getOverallScore, getRangeLabel, getTotalRoiAnnual } = useAudit();
  const [generating, setGenerating] = useState(false);
  
  // Get contact info from context (populated from checkout)
  const contactEmail = auditData.contact?.email || '';
  const contactName = auditData.contact?.firstName || auditData.contact?.name || '';
  const companyName = auditData.company || 'your company';

  const handleGenerateReport = async () => {
    if (!contactEmail) {
      toast.error('No email found. Please go back to checkout.');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        ...auditData,
        session_id: getAuditSessionId(),
        submitted_at: new Date().toISOString(),
        summary: {
          overall: getOverallScore(),
          range_label: getRangeLabel(getOverallScore()),
          total_roi_annual: getTotalRoiAnnual()
        }
      };

      const response = await axios.post(`${API}/audit/submit`, payload, {
        headers: auditSessionHeaders()
      });
      const auditId = response.data.id;
      
      // Track report generation
      trackReportGenerated(auditId, getOverallScore(), getTotalRoiAnnual());
      
      toast.success('Generating your personalized report...');
      
      // Navigate to report page
      setTimeout(() => {
        navigate(auditPath(`/report?audit_id=${auditId}`, location.pathname));
      }, 1500);
      
    } catch (error) {
      console.error('Error saving audit:', error);
      toast.error('Failed to save audit. Please try again.');
      setGenerating(false);
    }
  };

  return (
    <PageFrame pageNumber={9} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          {!generating ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-8"
            >
              {/* Header */}
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-extrabold text-primary" data-testid="action-title">
                  Ready for Your First Safe AI Workflow?
                </h1>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  We will turn your answers into a practical recommendation, trust guardrails, and a 14-day proof plan.
                </p>
              </div>

              {/* Info Preview */}
              <div className="bg-white rounded-lg shadow-lg p-8 space-y-4 mt-6">
                <div className="flex items-center justify-center gap-3 text-muted-foreground">
                  <Building2 className="h-5 w-5" />
                  <span className="text-lg">
                    Report for <strong className="text-primary">{companyName}</strong>
                  </span>
                </div>
                <div className="flex items-center justify-center gap-3 text-muted-foreground">
                  <Mail className="h-5 w-5" />
                  <span>
                    Sent to <strong className="text-primary">{contactEmail}</strong>
                  </span>
                </div>
                
                <div className="pt-6 border-t border-muted">
                  <p className="text-sm text-muted-foreground">
                    <strong>Don't forget to save!</strong> Click the "Save" button above to receive a resume link via email.
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <Button
                onClick={handleGenerateReport}
                size="lg"
                className="w-full py-8 text-xl font-bold bg-accent hover:bg-accent/90 text-accent-foreground gap-3 mt-6"
                data-testid="generate-report-button"
              >
                Generate My First Safe Workflow Report
              </Button>

              <p className="text-sm text-muted-foreground">
                This will take 10-15 seconds while we analyze your responses
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-12"
            >
              <ReportGenerationProgress companyName={companyName} />
            </motion.div>
          )}

          {/* Navigation */}
          {!generating && (
            <div className="pt-8">
              <NavigationControls currentPage={9} />
            </div>
          )}
        </div>
      </div>
    </PageFrame>
  );
}